import { Fragment, useState } from 'react';
import { observer } from 'mobx-react';
import { runInAction } from 'mobx';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { RefreshRounded } from '@mui/icons-material';
import { DateTime } from 'luxon';
import { RegisterZReportHistory } from '../api/admin';
import { useRegisterZHistory, ZReportSummary } from '../stores/FetcherStores/ZHistoryFetcher';
import LoadingWrapper from './LoadingWrapper';
import ZReportDetails from './ZReportDetails';
import { timestampToDate } from '../utils/date';

interface RegisterHistoryProps {
  companyId: string;
  register: Omit<RegisterZReportHistory, 'reports'> & { reports: ZReportSummary[] };
  showRegisterInfo: boolean;
}

const RegisterHistory = ({ companyId, register, showRegisterInfo }: RegisterHistoryProps) => {
  const [openReportId, setOpenReportId] = useState<string | null>(null);
  const sectionId = `register-${register.registerKey}`;
  const activatedAt = timestampToDate(register.activatedAt);

  const content = (
    <Box>
      {register.reports.length === 0 ? (
        <Typography>Det finns inga Z-rapporter att visa.</Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label={`Z-rapporter ${register.label || register.registerKey}`}>
            <TableHead>
              <TableRow>
                <TableCell>Id</TableCell>
                <TableCell>Datum</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {register.reports.map((report) => (
                <Fragment key={report.id}>
                  <TableRow sx={{ '& > td': { borderBottom: 'none' } }}>
                    <TableCell>{report.id}</TableCell>
                    <TableCell>
                      {report.time
                        ? DateTime.fromJSDate(report.time).toFormat('yyyy-MM-dd HH:mm')
                        : ''}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        aria-label={`Visa Z-rapport ${report.id}`}
                        aria-expanded={openReportId === report.id}
                        size="small"
                        onClick={() =>
                          setOpenReportId(openReportId === report.id ? null : report.id)
                        }
                      >
                        {openReportId === report.id ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0 }} colSpan={3}>
                      <Collapse in={openReportId === report.id} timeout="auto" unmountOnExit>
                        {openReportId === report.id && (
                          <ZReportDetails
                            companyId={companyId}
                            reportId={report.id}
                            registerKey={register.registerKey}
                          />
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );

  if (!showRegisterInfo) {
    return content;
  }

  return (
    <Accordion defaultExpanded={register.isCurrent}>
      <AccordionSummary
        expandIcon={<KeyboardArrowDownIcon />}
        id={`${sectionId}-header`}
        aria-controls={`${sectionId}-content`}
        sx={{ bgcolor: 'action.hover' }}
      >
        <Box>
          <Typography fontWeight="bold">
            {register.label || register.serial || register.registerKey}
            {' - '}
            {register.isCurrent ? 'Aktuellt kassaregister' : 'Tidigare kassaregister'}
            {` - ${register.reports.length} Z-rapporter`}
          </Typography>
          <Typography variant="body2">
            {`Aktiverad: ${
              activatedAt ? DateTime.fromJSDate(activatedAt).toFormat('yyyy-MM-dd HH:mm') : 'Okänt'
            }`}
            {register.serial && `   Serienummer: ${register.serial}`}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{content}</AccordionDetails>
    </Accordion>
  );
};

const CashRegisterZHistory = ({
  companyId,
  showPreviousRegisters = false,
}: {
  companyId: string;
  showPreviousRegisters?: boolean;
}) => {
  const history = useRegisterZHistory(companyId);
  const { data: registers, isLoading, error } = history;
  const visibleRegisters = registers?.filter(
    (register) => showPreviousRegisters || register.isCurrent,
  );

  return (
    <Paper sx={{ my: 2, p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h5">Z-rapporter</Typography>
        <IconButton
          aria-label="Uppdatera Z-rapporter"
          size="small"
          disabled={isLoading}
          onClick={() => {
            runInAction(() => {
              history.error = undefined;
            });
            history.refetch();
          }}
        >
          <RefreshRounded />
        </IconButton>
      </Box>
      {error && <Alert severity="error">Misslyckades med att hämta z-historik</Alert>}
      <LoadingWrapper loading={isLoading}>
        <Box>
          {!error &&
            (visibleRegisters?.length ? (
              visibleRegisters.map((register) => (
                <RegisterHistory
                  key={`${companyId}-${register.registerKey}-${register.isCurrent}`}
                  companyId={companyId}
                  register={register}
                  showRegisterInfo={showPreviousRegisters}
                />
              ))
            ) : (
              <Typography>Det finns inga Z-rapporter att visa.</Typography>
            ))}
        </Box>
      </LoadingWrapper>
    </Paper>
  );
};

export default observer(CashRegisterZHistory);
